/**
 * ClassificationBadge — the compact classification marking in the top bar,
 * next to the application wordmark (REQ-UI-058, badge form 2026-06-12).
 *
 * # 2026-06-12 — badge replaces the top/bottom banner
 *
 * Per architect direction the canvas-wide top + bottom classification banner (the
 * prior REQ-UI-058 form) was replaced by this single top-bar badge: a rounded
 * pill rendering the configured ``<CAPTION>`` (e.g. ``EXAMPLE MARKING A``)
 * immediately right of the wordmark. The classification is conveyed once, at
 * the top of every authenticated screen, rather than as two full-width bars.
 *
 * **Accreditation note.** Top + bottom IC banners are a common classified-
 * system marking expectation (the classification-banner standard); replacing them with a single badge is a
 * deliberate, captured decision (REQ-UI-058 + the design-system §classification
 * badge + chrome-spec.md amendment) — not an oversight. The fail-safe below is
 * retained so the marking is never absent or understated.
 *
 * # Colour (deployment-configured, theme-aware)
 *
 * Per ADR-042 D-3 the colour is **configuration**, not a function of the
 * deployment designation: the BFF serves a ``style`` the deployment declared
 * (green / amber / red / neutral) and the caption carries the meaning. Rendered
 * in a MUTED, THEME-AWARE palette tuned to the operator-tooling aesthetic (a
 * bg+fg pair per style; see the ``classification-badge`` tokens in
 * ``tailwind.config.js`` + ``--classification-badge-*`` in ``globals.css``).
 * Foregrounds are contrast-checked for WCAG-AA against each theme's top-bar
 * background (REQ-UI-046).
 *
 * # Fail-safe (CLAUDE.md §8.1)
 *
 * ``classification === null`` renders the high-alarm ``⚠ UNVERIFIED`` badge
 * (amber) — shown from first paint until ``GET /api/config`` resolves, kept if
 * that fetch ever fails, AND kept when the deployment configured no marking at
 * all (ADR-042 D-3 removed the per-designation caption/colour defaults). It
 * NEVER falls back to a real (possibly-too-low) marking, which would be
 * fail-*open*.
 *
 * Cross-references:
 *   - REQ-UI-058 (classification marking — badge form); REQ-UI-060 (config
 *     source); REQ-UI-046 (contrast); REQ-UI-059 (accessible naming).
 *   - chrome/use-classification.ts (the value source + post-MVP seam).
 *   - chrome/top-bar.tsx (mounts the badge beside the wordmark).
 *   - docs/engineering/detail/storyboards/shared/chrome-spec.md +
 *     design-system.md §"Meridian classification-badge palette".
 */
import { cn } from "@/lib/utils";

/**
 * The badge's configured colour (ADR-042 D-3).
 *
 * Names a colour, not a sensitivity: the meaning lives in the caption the
 * deployment configures alongside it. Previously this was keyed on the
 * deployment designation, which made the colour a function of it — exactly
 * the coupling ADR-042 removes.
 */
export type ClassificationStyle = "green" | "amber" | "red" | "neutral";

/** A resolved, deployment-declared marking for the badge. */
export interface ResolvedClassification {
  /** The configured badge colour. */
  readonly style: ClassificationStyle;
  /** All-caps caption (BFF-authoritative; may carry caveats). */
  readonly text: string;
}

/**
 * Per-style badge classes — theme-aware background + contrast-checked
 * foreground. Keyed on the configured style; the ``unverified`` fail-safe is
 * handled separately so it cannot be reached by a configured style value.
 */
const STYLE_CLASSES: Record<ClassificationStyle, string> = {
  green: "bg-classification-badge-green-bg text-classification-badge-green-fg",
  amber: "bg-classification-badge-amber-bg text-classification-badge-amber-fg",
  red: "bg-classification-badge-red-bg text-classification-badge-red-fg",
  neutral: "bg-classification-badge-neutral-bg text-classification-badge-neutral-fg",
};

/** Fail-safe alarm treatment + caption (see module docstring §Fail-safe). */
const UNVERIFIED_CLASSES =
  "bg-classification-badge-unverified-bg text-classification-badge-unverified-fg";
const UNVERIFIED_TEXT = "⚠ UNVERIFIED";

/**
 * Render the top-bar classification badge.
 *
 * @param classification - The resolved level + caption, or ``null`` for the
 *     fail-safe ``UNVERIFIED`` state.
 */
export function ClassificationBadge({
  classification,
}: {
  classification: ResolvedClassification | null;
}): React.ReactElement {
  const isVerified = classification !== null;
  // The caption stands alone: ADR-042 D-4 dropped the designation letter that
  // used to prefix it, because the designation is not a classification and
  // showing it here re-encoded the ordering the rename removes.
  const caption = isVerified ? classification.text : UNVERIFIED_TEXT;
  const colorClasses = isVerified ? STYLE_CLASSES[classification.style] : UNVERIFIED_CLASSES;
  const levelAttr = isVerified ? classification.style : "unverified";
  const ariaLabel = isVerified
    ? `Classification: ${classification.text}`
    : "Classification unverified — deployment configuration not loaded";

  return (
    <span
      role="note"
      data-testid="classification-badge"
      data-level={levelAttr}
      aria-label={ariaLabel}
      className={cn(
        "inline-flex shrink-0 select-none items-center rounded-full px-2.5 py-1",
        "text-caption font-semibold uppercase leading-none tracking-wider",
        colorClasses,
      )}
    >
      {caption}
    </span>
  );
}
